/**
 * Words for the printed receipt, in all three languages.
 *
 * Kept apart from the portal's own dictionary because the two answer different
 * questions. The portal is read by staff and speaks the two languages they
 * chose; a receipt is handed to a customer, and in Tbilisi that customer may
 * well want Georgian — which no member of staff needs the interface in.
 */
export type InvoiceLanguage = 'ka' | 'ru' | 'en';

export const INVOICE_LANGUAGES: InvoiceLanguage[] = ['ka', 'ru', 'en'];

export const INVOICE_LANGUAGE_LABELS: Record<InvoiceLanguage, string> = {
  ka: 'ქართული',
  ru: 'Русский',
  en: 'English',
};

type Key =
  | 'title' | 'customer' | 'vehicle' | 'servedBy' | 'service' | 'qty' | 'price'
  | 'total' | 'subtotal' | 'discount' | 'cashbackApplied' | 'toPay' | 'paidBy'
  | 'paid' | 'outstanding'
  | 'cashbackEarned' | 'matures' | 'points' | 'balanceAfter' | 'thanks' | 'pts';

const WORDS: Record<Key, Record<InvoiceLanguage, string>> = {
  title: { ka: 'ანგარიში', ru: 'СЧЁТ', en: 'INVOICE' },
  customer: { ka: 'კლიენტი', ru: 'Клиент', en: 'Customer' },
  vehicle: { ka: 'ავტომობილი', ru: 'Машина', en: 'Vehicle' },
  servedBy: { ka: 'მომსახურება: {name}', ru: 'Обслужил {name}', en: 'Served by {name}' },
  service: { ka: 'მომსახურება', ru: 'Услуга', en: 'Service' },
  qty: { ka: 'რაოდ.', ru: 'Кол-во', en: 'Qty' },
  price: { ka: 'ფასი', ru: 'Цена', en: 'Price' },
  total: { ka: 'ჯამი', ru: 'Сумма', en: 'Total' },
  subtotal: { ka: 'ჯამი მომსახურებით', ru: 'Итого по услугам', en: 'Subtotal' },
  discount: { ka: 'ფასდაკლება {percent}%', ru: 'Скидка {percent}%', en: 'Discount {percent}%' },
  cashbackApplied: { ka: 'ქეშბექით დაფარული', ru: 'Списано кэшбэка', en: 'Cashback applied' },
  toPay: { ka: 'გადასახდელი', ru: 'К оплате', en: 'To pay' },
  paidBy: { ka: 'გადახდა', ru: 'Оплата', en: 'Paid by' },
  paid: { ka: 'გადახდილია', ru: 'Внесено', en: 'Paid' },
  outstanding: { ka: 'დარჩენილი', ru: 'Остаток', en: 'Outstanding' },
  cashbackEarned: { ka: 'დარიცხული ქეშბექი', ru: 'Начислено кэшбэка', en: 'Cashback earned' },
  matures: { ka: '(გააქტიურდება მოგვიანებით)', ru: '(созреет позже)', en: '(matures later)' },
  points: { ka: 'ქულები', ru: 'Баллы', en: 'Points' },
  balanceAfter: { ka: 'ნაშთი შემდეგ:', ru: 'Баланс после:', en: 'Balance after:' },
  thanks: { ka: 'გმადლობთ. {company}', ru: 'Спасибо. {company}', en: 'Thank you. {company}' },
  pts: { ka: 'ქულა', ru: 'балл.', en: 'pts' },
};

/** Payment methods, which the receipt names in the customer's language too. */
const METHODS: Record<string, Record<InvoiceLanguage, string>> = {
  CASH: { ka: 'ნაღდი', ru: 'Наличные', en: 'Cash' },
  CARD: { ka: 'ბარათი', ru: 'Карта', en: 'Card' },
  BANK_TRANSFER: { ka: 'გადარიცხვა', ru: 'Перевод', en: 'Bank transfer' },
  CASHBACK: { ka: 'ქეშბექი', ru: 'Кэшбэк', en: 'Cashback' },
  OTHER: { ka: 'სხვა', ru: 'Другое', en: 'Other' },
};

export function invoiceText(language: InvoiceLanguage) {
  return (key: Key, params?: Record<string, string | number>): string => {
    const text = WORDS[key][language];
    if (!params) return text;
    return text.replace(/\{(\w+)\}/g, (whole, name: string) =>
      (params[name] === undefined ? whole : String(params[name])));
  };
}

export const invoiceMethod = (method: string, language: InvoiceLanguage): string =>
  METHODS[method]?.[language] ?? method;

/** Dates on the receipt follow the receipt's language, not the portal's. */
const DATE_LOCALES: Record<InvoiceLanguage, string> = {
  ka: 'ka-GE', ru: 'ru-RU', en: 'en-GB',
};

export function invoiceDateTime(value: string | Date, language: InvoiceLanguage): string {
  return new Intl.DateTimeFormat(DATE_LOCALES[language], {
    dateStyle: 'medium', timeStyle: 'short', hourCycle: 'h23', timeZone: 'Asia/Tbilisi',
  }).format(typeof value === 'string' ? new Date(value) : value);
}
